/**
 * FontPicker — sélecteur de police partagé entre l'Inspector et la toolbar
 * inline du calque texte.
 *
 * Utilise le store useFontStore pour la liste des polices (BASE_FONTS + polices
 * OS chargées via window.queryLocalFonts).
 *
 * Modes :
 * - "block" (par défaut) : bouton pleine largeur, dropdown sous le bouton
 *   (utilisé par Inspector)
 * - "compact" : bouton compact, dropdown qui peut s'afficher en portail
 *   (utilisé par la toolbar inline pour éviter le clipping)
 *
 * Le picker propose un mode "preview" : au survol d'une option, la police est
 * appliquée temporairement (utile pour visualiser sans cliquer). À la souris
 * qui sort du menu, la valeur d'origine est restaurée.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useFontStore, BASE_FONTS } from '../store/useFontStore';

export default function FontPicker({
    currentFont,
    onChange,
    onPreview,         // optionnel — appelé au survol pour preview
    onCancelPreview,   // optionnel — appelé à mouseLeave pour annuler
    mode = 'block',    // 'block' | 'compact'
    portal = false,    // true → dropdown rendu via createPortal dans body
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [originalFont, setOriginalFont] = useState(currentFont);
    const [dropdownPos, setDropdownPos] = useState(null);
    const buttonRef = useRef(null);
    const dropdownRef = useRef(null);

    // ⚠️ NE PAS faire `useFontStore(s => s.allFonts())` : allFonts est un getter
    // qui retourne un nouveau tableau à chaque appel → zustand voit un changement
    // de référence à chaque render → boucle infinie React #185.
    // À la place : on lit localFonts (référence stable tant que le store n'a pas
    // changé) et on calcule allFonts via useMemo dans le composant.
    const localFonts = useFontStore(s => s.localFonts);
    const googleFonts = useFontStore(s => s.googleFonts);
    const uploadedFonts = useFontStore(s => s.uploadedFonts);
    const isLoading = useFontStore(s => s.isLoading);
    const isLoadingGoogle = useFontStore(s => s.isLoadingGoogle);
    const isUploading = useFontStore(s => s.isUploading);
    const loadLocalFonts = useFontStore(s => s.loadLocalFonts);
    // Le refus de « Polices OS » (navigateur sans l'API, ou permission refusée) est
    // posé dans le store. Personne ne le lisait : depuis que ces deux échecs ne
    // passent plus par `alert()`, le clic ne produisait plus rien du tout.
    const localFontsErr = useFontStore(s => s.localFontsError);
    const loadGoogleFont = useFontStore(s => s.loadGoogleFont);
    const uploadFont = useFontStore(s => s.uploadFont);
    const removeUploadedFont = useFontStore(s => s.removeUploadedFont);
    const allFonts = useMemo(() => {
        const seen = new Set();
        const out = [];
        const add = (arr) => { for (const f of arr) if (!seen.has(f)) { seen.add(f); out.push(f); } };
        add(uploadedFonts);  // polices IMPORTÉES en tête de liste (marquées d'un point vert)
        add(BASE_FONTS);
        add(googleFonts);
        add(localFonts);
        return out;
    }, [localFonts, googleFonts, uploadedFonts]);
    const uploadedSet = useMemo(() => new Set(uploadedFonts.map(f => f.toLowerCase())), [uploadedFonts]);

    // ─── Recherche / chargement Google Fonts ───
    const [search, setSearch] = useState('');
    const [googleErr, setGoogleErr] = useState(null);
    const [uploadErr, setUploadErr] = useState(null);
    const fileInputRef = useRef(null);

    // Import d'un fichier de police (.ttf/.otf/.woff/.woff2).
    const handleFile = async (e) => {
        const file = e.target.files && e.target.files[0];
        if (e.target) e.target.value = ''; // permet de ré-importer le même fichier
        if (!file) return;
        setUploadErr(null);
        try {
            const fam = await uploadFont(file);
            handleSelect(fam);
        } catch (err) {
            setUploadErr(err.message || 'Import échoué');
        }
    };
    const filteredFonts = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return allFonts;
        return allFonts.filter(f => f.toLowerCase().includes(q));
    }, [search, allFonts]);
    // Si la recherche ne match aucune font connue → proposer chargement Google
    const canLoadGoogle = search.trim().length >= 2 && filteredFonts.length === 0;

    const handleLoadGoogle = async () => {
        setGoogleErr(null);
        try {
            const name = await loadGoogleFont(search.trim());
            onChange(name);
            setOriginalFont(name);
            setSearch('');
            setIsOpen(false);
        } catch (err) {
            setGoogleErr(err.message || 'Échec du chargement');
        }
    };

    // Click outside → close
    useEffect(() => {
        if (!isOpen) return;
        const handle = (e) => {
            if (dropdownRef.current?.contains(e.target)) return;
            if (buttonRef.current?.contains(e.target)) return;
            (onCancelPreview || onChange)?.(originalFont);
            setIsOpen(false);
        };
        document.addEventListener('mousedown', handle);
        return () => document.removeEventListener('mousedown', handle);
    }, [isOpen, originalFont, onChange, onCancelPreview]);

    // Position du dropdown en mode portal
    useEffect(() => {
        if (!isOpen || !portal || !buttonRef.current) return;
        const rect = buttonRef.current.getBoundingClientRect();
        const dropdownWidth = mode === 'compact' ? 220 : rect.width;
        const dropdownHeight = 320;
        let top = rect.bottom + 4;
        if (top + dropdownHeight > window.innerHeight - 16) {
            top = Math.max(8, rect.top - dropdownHeight - 4);
        }
        setDropdownPos({
            top,
            left: Math.min(rect.left, window.innerWidth - dropdownWidth - 8),
            width: dropdownWidth,
        });
    }, [isOpen, portal, mode]);

    const handleSelect = (font) => {
        onChange(font);
        setOriginalFont(font);
        setIsOpen(false);
    };

    const handleHover = (font) => {
        (onPreview || onChange)?.(font);
    };

    const handleLeave = () => {
        (onCancelPreview || onChange)?.(originalFont);
    };

    const buttonClass = mode === 'compact'
        ? "h-8 px-2.5 bg-black/40 border border-white/10 rounded text-[11px] text-forge-text outline-none hover:bg-black/60 flex items-center gap-1.5 min-w-[160px] max-w-[220px] truncate"
        : "w-full bg-black/40 shadow-inner border border-white/5 rounded-lg px-3 py-2 text-sm text-left flex justify-between items-center hover:border-white/10 hover:bg-white/5 transition-all outline-none";

    const dropdownContent = (
        <ul
            ref={dropdownRef}
            data-fontpicker-dropdown="true"
            // Avant : bg-black/90 → en thème clair le dropdown restait noir
            // avec texte noir dessus = illisible. bg-forge-surface suit le
            // thème (sombre = surface foncée, clair = surface quasi blanche).
            className="bg-forge-surface/95 backdrop-blur-md border border-white/10 rounded-xl shadow-2xl flex flex-col max-h-[320px] overflow-hidden"
            style={portal ? {
                position: 'fixed',
                top: dropdownPos?.top,
                left: dropdownPos?.left,
                width: dropdownPos?.width,
                // 145 = palier « popover transitoire » : ouvrable depuis une
                // modale (140), fermé au clic extérieur — jamais persistant.
                zIndex: 145,
            } : undefined}
            onMouseLeave={handleLeave}
        >
            {/* Barre de recherche : filtre la liste ET permet de charger
                une font Google Fonts à la volée si elle n'est pas trouvée. */}
            <div className="px-2 pt-2 pb-1 border-b border-white/10">
                <input
                    type="text"
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setGoogleErr(null); }}
                    placeholder="Rechercher ou charger une font Google…"
                    autoFocus
                    className="w-full bg-black/40 border border-white/10 rounded px-2 py-1.5 text-[11px] text-white/85 placeholder-white/30 outline-none focus:border-white/30 transition-colors"
                />
            </div>
            <div className="overflow-y-auto custom-scrollbar flex-1 py-1">
                {filteredFonts.map(font => (
                    <li
                        key={font}
                        onMouseEnter={() => handleHover(font)}
                        onClick={() => handleSelect(font)}
                        className={`group/font flex items-center justify-between gap-2 px-3 py-1.5 text-xs cursor-pointer transition-all ${currentFont === font ? 'bg-white/20 text-white font-bold' : 'text-white/70 hover:bg-white/10 hover:text-white'}`}
                        style={{ fontFamily: `"${font}", sans-serif`, fontSize: '13px' }}
                    >
                        <span className="truncate flex items-center gap-1.5 min-w-0">
                            {uploadedSet.has(font.toLowerCase()) && (
                                <span
                                    title="Police importée — embarquée à l'export"
                                    className="shrink-0 inline-block w-2 h-2 rounded-full bg-green-400 shadow-[0_0_4px_rgba(74,222,128,0.7)]"
                                />
                            )}
                            <span className="truncate">{font}</span>
                        </span>
                        {uploadedSet.has(font.toLowerCase()) && (
                            <button
                                type="button"
                                title="Supprimer cette police importée" aria-label="Supprimer cette police importée"
                                onClick={(e) => { e.stopPropagation(); removeUploadedFont(font); }}
                                className="shrink-0 opacity-0 group-hover/font:opacity-100 text-white/40 hover:text-red-300 text-[13px] leading-none px-1"
                            >
                                ×
                            </button>
                        )}
                    </li>
                ))}
                {canLoadGoogle && (
                    <li className="px-3 py-3 flex flex-col gap-1.5 bg-blue-500/[0.06]">
                        <p className="text-[10px] text-white/55 font-mono leading-relaxed">
                            Aucune font trouvée pour <span className="text-white/85 font-bold">"{search.trim()}"</span>.
                        </p>
                        <button
                            type="button"
                            onClick={handleLoadGoogle}
                            disabled={isLoadingGoogle}
                            className="text-[10px] bg-blue-500/20 hover:bg-blue-500/35 border border-blue-500/40 hover:border-blue-500/60 px-2.5 py-1.5 rounded font-mono uppercase tracking-wider text-blue-200 transition-colors disabled:opacity-50 disabled:cursor-wait"
                        >
                            {isLoadingGoogle ? '↻ Chargement…' : `↓ Charger "${search.trim()}" depuis Google Fonts`}
                        </button>
                        {googleErr && (
                            <p className="text-[9px] text-red-300 font-mono mt-1">⚠ {googleErr}</p>
                        )}
                        <p className="text-[8px] text-white/30 font-mono mt-1">
                            Astuce : tape le nom exact (sensible à la casse), ex. "Bebas Neue", "Inter", "Roboto Slab".
                        </p>
                    </li>
                )}
            </div>
            <div className="border-t border-white/10 p-2 bg-black/40 flex flex-col gap-1.5">
                <button
                    type="button"
                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                    disabled={isUploading}
                    className="w-full text-[10px] bg-white/5 hover:bg-white hover:text-black transition-colors border border-white/10 py-1.5 rounded font-mono uppercase disabled:opacity-50 disabled:cursor-wait"
                >
                    {isUploading ? "Import…" : "+ Importer une police (.ttf, .otf)"}
                </button>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".ttf,.otf,.woff,.woff2,font/ttf,font/otf,font/woff,font/woff2"
                    className="hidden"
                    onChange={handleFile}
                />
                {localFonts.length === 0 && (
                    <button
                        onClick={loadLocalFonts}
                        disabled={isLoading}
                        className="w-full text-[10px] bg-white/5 hover:bg-white hover:text-black transition-colors border border-white/10 py-1.5 rounded font-mono uppercase"
                    >
                        {isLoading ? "Chargement..." : "+ Polices OS (Chrome)"}
                    </button>
                )}
                {uploadErr && (
                    <p className="text-[9px] text-red-300 font-mono">⚠ {uploadErr}</p>
                )}
                {localFontsErr && (
                    <p className="text-[9px] text-red-300 font-mono leading-snug">⚠ {localFontsErr}</p>
                )}
                <p className="text-[8px] text-white/35 font-mono leading-snug flex items-center gap-1.5">
                    <span className="shrink-0 inline-block w-2 h-2 rounded-full bg-green-400" />
                    importée = embarquée au PDF. « OS (Chrome) » = affichée seulement, non garantie à l'export.
                </p>
            </div>
        </ul>
    );

    return (
        <div className={mode === 'compact' ? 'relative' : 'relative w-full'}>
            <button
                ref={buttonRef}
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    setOriginalFont(currentFont);
                    setIsOpen(!isOpen);
                }}
                className={buttonClass}
            >
                <span className="truncate text-white" style={{ fontFamily: `"${currentFont}", sans-serif` }}>
                    {currentFont || '— Police —'}
                </span>
                <span className="text-white/30 text-[10px]">▼</span>
            </button>

            {isOpen && (portal
                ? createPortal(dropdownContent, document.body)
                : (
                    <div className="absolute left-0 right-0 mt-1.5 z-50">
                        {dropdownContent}
                    </div>
                )
            )}
        </div>
    );
}
